/**
 * GUARD-008: manejo central de errores de callables.
 *
 * Ante un `permission-denied`:
 *  - limpia datos sensibles del estado local (evitar que queden en pantalla),
 *  - devuelve el error para que la UI muestre un mensaje genérico.
 *
 * Uso:
 *   const res = await handleCallableError(() => fn(data), { onDenied });
 */

export const isPermissionDenied = (error) => {
  if (!error) return false;
  const code = error.code || error.message || "";
  return (
    code.includes("permission-denied") || code.includes("PERMISSION_DENIED") ||
    code.includes("unauthenticated") || code.includes("UNAUTHENTICATED")
  );
};

export const PERMISSION_DENIED_EVENT = "autoapp:permission-denied";

/**
 * Invalida el contexto operativo ante una denegación del backend. El
 * AuthProvider vuelve a resolver permisos y, al cambiar su key operacional,
 * desmonta formularios, cachés y listeners del contexto anterior.
 */
export const notifyPermissionDenied = (error) => {
  if (!isPermissionDenied(error) || typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(PERMISSION_DENIED_EVENT, { detail: { code: error.code } }),
  );
};

export const handleCallableError = async (fn, opts = {}) => {
  const { onDenied, onError } = opts;
  try {
    return await fn();
  } catch (error) {
    if (isPermissionDenied(error)) {
      console.error("Acceso denegado por el backend:", error);
      notifyPermissionDenied(error);
      if (typeof onDenied === "function") onDenied(error);
      throw error;
    }
    console.error("Error de Cloud Function:", error);
    if (typeof onError === "function") onError(error);
    throw error;
  }
};
